import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { CoverImage, RankingSelector, SidebarContentView } from './components';
import { MiniligueProfilePic } from './components/MiniligueProfilePic';

import { Loader, Tabs } from '@/components';
import {
  Layout,
  ParticipantsAvatarStack,
  ParticipantsModal,
} from '@/components/GenericsTournaments';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { getViewMl } from '@/services/minileagues';
import { DateGDT } from '@/types';
import {
  DatosParticipanteMiniLigasTO,
  EnumCoverMinileagues,
  FechaMiniLigaTO,
  MiniLigasTO,
  PartidoMiniLigaTO,
  ResponseVerML,
} from '@/types/minileagues';
import { GENERICS_WORDS, getPhotoUserId } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

export const ViewML: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { tab, handleTabChanges } = useHandleChangesTabs();
  const { isDesktop, isMobile } = useIsDesktop();
  const { tourneyData, updateTourney } = useTourney();
  const { userInitialData } = useAuth();

  const [showModalParticipant, setShowModalParticipant] = useState(false);

  const isFinalDes = location.state.isFinalDes;

  const setInitData = useCallback(
    (response: ResponseVerML) => {
      // Mapeo FechaMiniLigaTO a DateGDT
      const mapToDateGDT = (item: FechaMiniLigaTO): DateGDT | undefined => {
        if (!item) return undefined;

        return {
          id: item.id || 0,
          nombre: item.nF || '',
          orden: item.ordF || 0,
          isPublicada: Number(item.idE) === 2,
        };
      };

      // array de fechas
      const mapMatchDays = (
        matchDays: FechaMiniLigaTO[] | null | undefined
      ): DateGDT[] => {
        if (!matchDays || !Array.isArray(matchDays)) return [];

        // Elimino los valores undefined después del mapeo
        return matchDays.map(mapToDateGDT).filter(Boolean) as DateGDT[];
      };

      const rankingAvailable = mapMatchDays(response.fechasConfML);

      // Obtenemos el día de partido inicial
      const firstMatchDay = rankingAvailable.find(
        (day) => day.orden === response.ordenFechaMiniligaActual
      );

      // Actualizamos el torneo
      updateTourney({
        tourney: response,
        initDay: response.ordenFechaMiniligaActual,
        matchDay: firstMatchDay,
        rankingAvailable,
        isOwner: response.miniliga?.isOwner,
      });
    },
    [updateTourney]
  );

  const { isLoading, data } = useQuery(
    'RequestViewMinileagues',
    async () => await getViewMl(tourneyData.id),
    {
      onSuccess: async (response: ResponseVerML) => {
        if (response.estado) {
          const idUsuarioLogueado = userInitialData?.idUsuarioDT;
          const responseNormalizado = mapearResponseNormalizado(
            idUsuarioLogueado,
            response
          );
          setInitData(responseNormalizado);
        } else {
          console.error(
            `Error obteniendo datos de ${GENERICS_WORDS.minileagues}.`
          );
        }
      },
      onError: (error) => {
        console.error(
          `Error obteniendo datos de ${GENERICS_WORDS.minileagues}.`,
          error
        );
        throw new Error('Invalid response state');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: Boolean(tourneyData.id),
    }
  );

  const normalizarPartido = (
    partido: PartidoMiniLigaTO,
    idUsuarioLogueado?: number
  ): PartidoMiniLigaTO => {
    if (!idUsuarioLogueado || !partido?.p1 || !partido?.p2) {
      return partido;
    }

    const p1EsUsuario = partido.p1.part?.idUsuario === idUsuarioLogueado;
    const p2EsUsuario = partido.p2.part?.idUsuario === idUsuarioLogueado;

    // Si el usuario está en p2 → swap
    if (!p1EsUsuario && p2EsUsuario) {
      return {
        ...partido,
        p1: partido.p2,
        p2: partido.p1,
        idP1: partido.idP2,
        idP2: partido.idP1,
        ptsP1: partido.ptsP2,
        ptsP2: partido.ptsP1,
      };
    }

    return partido;
  };

  const mapearResponseNormalizado = (
    idUsuarioLogueado: number | undefined,
    response: ResponseVerML
  ) => {
    return {
      ...response,
      partidosFecha: response.partidosFecha
        ? Object.fromEntries(
            Object.entries(response.partidosFecha).map(([key, partidos]) => [
              key,
              partidos.map((p) => normalizarPartido(p, idUsuarioLogueado)),
            ])
          )
        : response.partidosFecha,
    };
  };

  const minileague: MiniLigasTO | undefined = useMemo(() => {
    if (data?.estado && !isLoading && data.miniliga) {
      return data.miniliga;
    }
  }, [data, isLoading]);

  // tabs
  const tabsTeamData: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: GENERICS_WORDS.fixture, disabled: false },
      { name: GENERICS_WORDS.positions, disabled: false },
    ];
  }, []);

  // set de solapa posiciones oninit
  useEffect(() => {
    handleTabChanges(TabType.FirstTab);
    updateTourney({ mainTab: TabType.FirstTab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHandleTabsChange = useCallback(
    (tab: TabType) => {
      handleTabChanges(tab);
      updateTourney({ mainTab: tab });
    },
    [handleTabChanges, updateTourney]
  );

  // const veda = useMemo(() => {
  //   if (data && data.fechasMiniLiga) {
  //     const fechaMLSelecc = data.fechasMiniLiga.find(
  //       (day) => day.id === tourneyData.matchDay?.id
  //     );

  //     return (
  //       fechaMLSelecc &&
  //       data.ordenFechaMiniligaActual === fechaMLSelecc.ordF &&
  //       data.veda &&
  //       data.estadoFechaMiniligaActual === 'NO_PUBLICADA'
  //     );
  //   }
  // }, [data, tourneyData.matchDay?.id]);

  // const coverContext = useMemo(() => {
  //   return EnumCoverMinileagues.PassFinal;
  // }, [veda]);

  // la foto propia puede llegar desactualizada desde el servicio de participantes
  const ownPhotoUserId = useMemo(
    () => getPhotoUserId(userInitialData?.pathImagenPerfil),
    [userInitialData?.pathImagenPerfil]
  );

  const getParticipantPhoto = useCallback(
    (item: DatosParticipanteMiniLigasTO) =>
      ownPhotoUserId && getPhotoUserId(item.part?.fotoPerfil) === ownPhotoUserId
        ? userInitialData?.pathImagenPerfil
        : item.part?.fotoPerfil,
    [ownPhotoUserId, userInitialData?.pathImagenPerfil]
  );

  return (
    <>
      <Loader isVisible={!minileague} timeout={!minileague} />
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.miniligas}
        backButton={isDesktop && true}
        backButtonAction={() =>
          navigate(ROUTES.minileagues.home, {
            state: { comesFromFinal: location.state.comesFromFinal ?? false },
          })
        }
        className="px-4"
        coverTop={() => {
          return (
            <CoverImage
              context={
                isFinalDes
                  ? EnumCoverMinileagues.FinalEnd
                  : EnumCoverMinileagues.PassFinal
              }
            />
          );
        }}
        main={() => (
          <div className="lg:pr-4">
            {isDesktop && (
              <div className="max-h-[180px] h-screen">
                <div className="flex">
                  <MiniligueProfilePic />
                  <div>
                    <h1 className="font-bold text-2xl mt-4">
                      {minileague?.nombreML}
                    </h1>
                    <ParticipantsAvatarStack
                      participants={(minileague?.partis || []).map(
                        (item, index) => ({
                          key: index,
                          alt: String(item.part?.idUsuario),
                          dtName:
                            item.part?.n +
                            ' ' +
                            String(item.part?.a).split(' ')[0],
                          photoUrl: getParticipantPhoto(item),
                        })
                      )}
                      total={minileague?.cantidadParticipantes ?? 0}
                      onClick={() => setShowModalParticipant(true)}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="lg:bg-white lg:rounded-xl">
              <RankingSelector />
            </div>

            {/* //MODAL PARTICIPANTES */}
            <ParticipantsModal
              isOpen={showModalParticipant}
              participants={(minileague?.partis || []).map((item) => ({
                key: `${item?.part?.idUsuario}`,
                alt: String(item.part?.idUsuario),
                dtName: item.part?.n + ' ' + String(item.part?.a).split(' ')[0],
                photoUrl: getParticipantPhoto(item),
                title: item.part?.n + ' ' + item.part?.a,
                subtitle: item.ne,
                extra: item.p,
              }))}
              total={minileague?.cantidadParticipantes}
              onClose={() => setShowModalParticipant(false)}
            />
          </div>
        )}
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.minileagues.home, {
                state: { comesFromFinal: true },
              });
            },
          },
          centerContent: {
            title: minileague?.nombreML,
          },
        }}
        sidebar={() => isDesktop && <SidebarContentView />}
        tabs={() => {
          return isMobile ? (
            <Tabs
              activeTab={tab}
              tabs={tabsTeamData}
              onClick={onHandleTabsChange}
            />
          ) : null;
        }}
      />
    </>
  );
};
